import React, { useState, useMemo, useEffect } from "react";
import { C } from "./constants";
import {
  Card, Btn, Field, Modal, ModuleHeader, KPICard, TabBar,
  StatusBadge, DataTable, useToast, filterByOwner
} from "./erp-shared";
import { MonthlyReportButton } from "./erp-monthly-report";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid
} from "recharts";
import {
  Home, LayoutDashboard, Building2, Users, Calendar,
  BookOpen, Star, GraduationCap, Package, Mic,
  FileText, AlertTriangle, Plus, Search, Download,
  BarChart3, PieChart as PieChartIcon,
  ClipboardList, UserCheck, Briefcase
} from "lucide-react";
import {
  getHousingResidences, getHousingWorkers, getHousingPreachers,
  getHousingVisits, getHousingPrograms, getHousingNewMuslims,
  getHousingEducation, getHousingMaterials,
  createHousingResidence, createHousingWorker, createHousingPreacher,
  createHousingVisit, createHousingProgram, createHousingNewMuslim,
  updateHousingEducation, createHousingMaterial,
} from "./supabaseClient";

/* ✦ محوّلات صفوف DB (snake_case) → المفاتيح التي تقرأها الواجهة. */
function mapResidenceRow(r) {
  return {
    id: r.id,
    code: r.code || "",
    name: r.name || "",
    district: r.district || "",
    address: r.address || "",
    capacity: Number(r.capacity) || 0,
    currentOccupancy: Number(r.current_occupancy) || 0,
    supervisorName: r.supervisor_name || "",
    supervisorPhone: r.supervisor_phone || "",
    type: r.type || "",
    status: r.status || "",
    notes: r.notes || "",
  };
}

function mapWorkerRow(r) {
  return {
    id: r.id,
    residenceId: r.residence_id || "",
    residenceName: r.residence_name || "",
    name: r.name || "",
    nationality: r.nationality || "",
    language: r.language || "",
    age: Number(r.age) || 0,
    job: r.job || "",
    employer: r.employer || "",
    phone: r.phone || "",
    religion: r.religion || "",
    entryDate: r.entry_date || "",
    status: r.status || "",
    notes: r.notes || "",
  };
}

function mapPreacherRow(r) {
  return {
    id: r.id,
    name: r.name || "",
    phone: r.phone || "",
    email: r.email || "",
    languages: Array.isArray(r.languages) ? r.languages : [],
    assignedResidences: Array.isArray(r.assigned_residences) ? r.assigned_residences : [],
    status: r.status || "",
  };
}

function mapVisitRow(r) {
  return {
    id: r.id,
    residenceId: r.residence_id || "",
    residenceName: r.residence_name || "",
    date: r.date || "",
    time: r.time || "",
    preacherId: r.preacher_id || "",
    preacherName: r.preacher_name || "",
    type: r.type || "",
    attendeesCount: Number(r.attendees_count) || 0,
    topicsCovered: r.topics_covered || "",
    outcome: r.outcome || "",
    notes: r.notes || "",
  };
}

function mapProgramRow(r) {
  return {
    id: r.id,
    residenceId: r.residence_id || "",
    residenceName: r.residence_name || "",
    name: r.name || "",
    type: r.type || "",
    preacherName: r.preacher_name || "",
    startDate: r.start_date || "",
    sessionsCount: Number(r.sessions_count) || 0,
    beneficiariesCount: Number(r.beneficiaries_count) || 0,
    status: r.status || "",
    notes: r.notes || "",
  };
}

function mapNewMuslimRow(r) {
  const cert = r.certificate || {};
  return {
    id: r.id,
    residenceId: r.residence_id || "",
    residenceName: r.residence_name || "",
    nameBefore: r.name_before || "",
    nameAfter: r.name_after || "",
    nationality: r.nationality || "",
    language: r.language || "",
    conversionDate: r.conversion_date || "",
    preacherId: r.preacher_id || "",
    preacherName: r.preacher_name || "",
    educationProgram: r.education_program || "",
    followUpStatus: r.follow_up_status || "",
    certificate: {
      issued: !!cert.issued,
      number: cert.number || "",
      issueDate: cert.issueDate || "",
      delivered: !!cert.delivered,
    },
    notes: r.notes || "",
  };
}

function mapEducationRow(r) {
  return {
    id: r.id,
    newMuslimId: r.new_muslim_id || "",
    beneficiaryName: r.beneficiary_name || "",
    residenceName: r.residence_name || "",
    shahada: Number(r.shahada) || 0,
    taharah: Number(r.taharah) || 0,
    salah: Number(r.salah) || 0,
    iman: Number(r.iman) || 0,
    recitation: Number(r.recitation) || 0,
    lastUpdate: r.last_update || "",
  };
}

function mapMaterialRow(r) {
  return {
    id: r.id,
    residenceId: r.residence_id || "",
    residenceName: r.residence_name || "",
    item: r.item || "",
    category: r.category || "",
    quantity: Number(r.quantity) || 0,
    distributedDate: r.distributed_date || "",
    distributedBy: r.distributed_by || "",
    recipientsCount: Number(r.recipients_count) || 0,
    notes: r.notes || "",
  };
}

const TODAY_REF = new Date("2026-06-19");

/* ═════════════════════════════════════════════
   ✦ Residences
   ═════════════════════════════════════════════ */
const RESIDENCE_TYPES = ["سكن جماعي", "شقق"];
const RESIDENCE_STATUSES = [
  { key: "نشط",           label: "نشط",           color: C.success },
  { key: "ممتلئ",         label: "ممتلئ",         color: C.accent },
  { key: "تحت الصيانة",   label: "تحت الصيانة",   color: C.warning },
  { key: "مغلق",          label: "مغلق",          color: C.danger },
];
const DISTRICTS = ["النفل", "الصفراء", "السلام", "الريان", "الإسكان", "الرحاب", "العزيزية", "الخليج"];


const INITIAL_RESIDENCE = { code: "", name: "", district: "", address: "", capacity: "", currentOccupancy: "", supervisorName: "", supervisorPhone: "", type: "سكن جماعي", status: "نشط", notes: "" };

/* ═════════════════════════════════════════════
   ✦ Workers (العاملات)
   ═════════════════════════════════════════════ */
const NATIONALITIES = ["إندونيسية", "فلبينية", "بنغلاديشية", "إثيوبية", "كينية", "أوغندية", "سريلانكية", "هندية"];
const LANGUAGES = ["إندونيسية", "تاغالوغ", "بنغالية", "أمهرية", "سواحلية", "إنجليزية", "أردو", "هندية"];
const RELIGIONS = ["مسلمة", "غير مسلمة"];
const WORKER_STATUSES = [
  { key: "مقيمة", label: "مقيمة", color: C.success },
  { key: "منتقلة", label: "منتقلة", color: C.accent },
  { key: "مغادرة", label: "مغادرة", color: C.textLight },
];
const JOBS = ["عاملة منزلية", "مربية", "طاهية", "خادمة", "ممرضة منزلية"];


const INITIAL_WORKER = { residenceId: "", name: "", nationality: "إندونيسية", language: "إندونيسية", age: "", job: "عاملة منزلية", employer: "", phone: "", religion: "غير مسلمة", entryDate: "", status: "مقيمة", notes: "" };

/* ═════════════════════════════════════════════
   ✦ Preachers (الداعيات)
   ═════════════════════════════════════════════ */
const PREACHER_STATUSES = [
  { key: "نشطة",   label: "نشطة",   color: C.success },
  { key: "متوقفة", label: "متوقفة", color: C.danger },
];


const INITIAL_PREACHER = { name: "", phone: "", email: "", languages: "", assignedResidences: "", status: "نشطة" };

/* ═════════════════════════════════════════════
   ✦ Visits (الزيارات)
   ═════════════════════════════════════════════ */
const VISIT_TYPES = ["دعوية", "تعليمية", "متابعة", "توزيع مواد"];


const INITIAL_VISIT = { residenceId: "", date: "", time: "", preacherId: "", type: "دعوية", attendeesCount: 0, topicsCovered: "", outcome: "", notes: "" };

/* ═════════════════════════════════════════════
   ✦ Programs (الدروس والبرامج)
   ═════════════════════════════════════════════ */
const PROGRAM_TYPES = ["درس ديني", "دورة لغة", "تعليم قرآن", "برنامج تعريفي", "ورشة"];
const PROGRAM_STATUSES = [
  { key: "مخطط",  label: "مخطط",  color: C.textLight },
  { key: "جاري",  label: "جاري",  color: C.success },
  { key: "منتهي", label: "منتهي", color: "#7c3aed" },
];


const INITIAL_PROGRAM = { residenceId: "", name: "", type: "درس ديني", preacherName: "", startDate: "", sessionsCount: 0, beneficiariesCount: 0, status: "مخطط", notes: "" };

/* ═════════════════════════════════════════════
   ✦ New Muslims (المسلمات الجديدات)
   ═════════════════════════════════════════════ */
const FOLLOWUP_STATUSES = [
  { key: "تحت المتابعة", label: "تحت المتابعة", color: C.teal },
  { key: "مكتمل",        label: "مكتمل",        color: C.success },
  { key: "منقطع",        label: "منقطع",        color: C.danger },
];


const INITIAL_NEW_MUSLIM = { residenceId: "", nameBefore: "", nameAfter: "", nationality: "إندونيسية", language: "إندونيسية", conversionDate: "", preacherId: "", educationProgram: "", followUpStatus: "تحت المتابعة", certIssued: false, certNumber: "", certIssueDate: "", certDelivered: false, notes: "" };

/* ═════════════════════════════════════════════
   ✦ Education (التعليم والمتابعة)
   ═════════════════════════════════════════════ */

/* ═════════════════════════════════════════════
   ✦ Materials (المواد والهدايا)
   ═════════════════════════════════════════════ */
const MATERIAL_CATEGORIES = ["مصحف", "كتيب", "مطوية", "هدية", "ملابس", "أخرى"];


const INITIAL_MATERIAL = { residenceId: "", item: "", category: "مصحف", quantity: 0, distributedDate: "", distributedBy: "", recipientsCount: 0, notes: "" };

/* ═════════════════════════════════════════════
   ✦ Helpers
   ═════════════════════════════════════════════ */
function daysSince(dateStr) {
  if (!dateStr) return 0;
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return 0;
  return Math.max(0, Math.floor((TODAY_REF - d) / 86400000));
}

function monthLabelAR(monthIdx) {
  const months = ["يناير","فبراير","مارس","أبريل","مايو","يونيو","يوليو","أغسطس","سبتمبر","أكتوبر","نوفمبر","ديسمبر"];
  return months[monthIdx];
}

/* ═════════════════════════════════════════════
   ✦ MAIN COMPONENT
   ═════════════════════════════════════════════ */
export default function HousingPage({ user, perms }) {
  const [tab, setTab] = useState("overview");
  const [residences, setResidences] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [newMuslims, setNewMuslims] = useState([]);
  const [education, setEducation] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [preachers, setPreachers] = useState([]);
  const [showToast, ToastView] = useToast();

  useEffect(() => { getHousingResidences().then(({ data }) => setResidences((data || []).map(mapResidenceRow))).catch(() => {}); }, []);
  useEffect(() => { getHousingWorkers().then(({ data }) => setWorkers((data || []).map(mapWorkerRow))).catch(() => {}); }, []);
  useEffect(() => { getHousingPreachers().then(({ data }) => setPreachers((data || []).map(mapPreacherRow))).catch(() => {}); }, []);
  useEffect(() => { getHousingVisits().then(({ data }) => setVisits((data || []).map(mapVisitRow))).catch(() => {}); }, []);
  useEffect(() => { getHousingPrograms().then(({ data }) => setPrograms((data || []).map(mapProgramRow))).catch(() => {}); }, []);
  useEffect(() => { getHousingNewMuslims().then(({ data }) => setNewMuslims((data || []).map(mapNewMuslimRow))).catch(() => {}); }, []);
  useEffect(() => { getHousingEducation().then(({ data }) => setEducation((data || []).map(mapEducationRow))).catch(() => {}); }, []);
  useEffect(() => { getHousingMaterials().then(({ data }) => setMaterials((data || []).map(mapMaterialRow))).catch(() => {}); }, []);

  const reportSources = [
    {
      label: "الزيارات", mode: "monthly", dateField: "date", rows: visits,
      columns: [
        { key: "residenceName", label: "المسكن" },
        { key: "date", label: "التاريخ" },
        { key: "preacherName", label: "الداعية" },
        { key: "type", label: "النوع" },
        { key: "attendeesCount", label: "عدد الحاضرات" },
        { key: "outcome", label: "الأثر" },
      ],
    },
    {
      label: "الدروس والبرامج", mode: "monthly", dateField: "startDate", rows: programs,
      columns: [
        { key: "residenceName", label: "المسكن" },
        { key: "name", label: "البرنامج" },
        { key: "type", label: "النوع" },
        { key: "startDate", label: "البداية" },
        { key: "beneficiariesCount", label: "المستفيدات" },
        { key: "status", label: "الحالة" },
      ],
    },
    {
      label: "المسلمات الجديدات", mode: "monthly", dateField: "conversionDate", rows: newMuslims,
      columns: [
        { key: "residenceName", label: "المسكن" },
        { key: "nameAfter", label: "الاسم" },
        { key: "nationality", label: "الجنسية" },
        { key: "conversionDate", label: "تاريخ الإسلام" },
        { key: "preacherName", label: "الداعية" },
        { key: "followUpStatus", label: "حالة المتابعة" },
      ],
    },
    {
      label: "المواد والهدايا", mode: "monthly", dateField: "distributedDate", rows: materials,
      columns: [
        { key: "residenceName", label: "المسكن" },
        { key: "item", label: "المادة" },
        { key: "category", label: "التصنيف" },
        { key: "quantity", label: "الكمية" },
        { key: "distributedDate", label: "تاريخ التوزيع" },
        { key: "recipientsCount", label: "المستفيدات" },
      ],
    },
    { label: "المساكن", mode: "cumulative", rows: residences },
    { label: "العاملات", mode: "cumulative", rows: workers },
    { label: "الداعيات", mode: "cumulative", rows: preachers },
    { label: "التعليم والمتابعة", mode: "cumulative", rows: education },
  ];

  const tabs = [
    { key: "overview",   label: "لوحة التحكم",       icon: LayoutDashboard },
    { key: "residences", label: "المساكن",            icon: Building2 },
    { key: "workers",    label: "العاملات",           icon: Users },
    { key: "visits",     label: "الزيارات",           icon: Calendar },
    { key: "programs",   label: "الدروس والبرامج",   icon: BookOpen },
    { key: "newMuslims", label: "المسلمات الجديدات", icon: Star },
    { key: "education",  label: "التعليم والمتابعة", icon: GraduationCap },
    { key: "materials",  label: "المواد والهدايا",   icon: Package },
    { key: "preachers",  label: "الداعيات",           icon: Mic },
    { key: "reports",    label: "التقارير",           icon: FileText },
    { key: "alerts",     label: "التنبيهات",          icon: AlertTriangle },
  ];

  return (
    <div>
      {ToastView}
      <ModuleHeader icon={Home} title="إسكان العاملات" subtitle="إدارة مساكن العاملات والمتابعة الدعوية والتعليمية">
        <MonthlyReportButton moduleTitle="إسكان العاملات" sources={reportSources} />
      </ModuleHeader>
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab === "overview"   && <OverviewTab residences={residences} workers={workers} visits={visits} programs={programs} newMuslims={newMuslims} preachers={preachers} />}
      {tab === "residences" && <ResidencesTab residences={residences} setResidences={setResidences} showToast={showToast} />}
      {tab === "workers"    && <WorkersTab residences={residences} workers={workers} setWorkers={setWorkers} showToast={showToast} />}
      {tab === "visits"     && <VisitsTab residences={residences} preachers={preachers} visits={visits} setVisits={setVisits} user={user} perms={perms} showToast={showToast} />}
      {tab === "programs"   && <ProgramsTab residences={residences} preachers={preachers} programs={programs} setPrograms={setPrograms} showToast={showToast} />}
      {tab === "newMuslims" && <NewMuslimsTab residences={residences} preachers={preachers} newMuslims={newMuslims} setNewMuslims={setNewMuslims} user={user} perms={perms} showToast={showToast} />}
      {tab === "education"  && <EducationTab education={education} setEducation={setEducation} showToast={showToast} />}
      {tab === "materials"  && <MaterialsTab residences={residences} materials={materials} setMaterials={setMaterials} showToast={showToast} />}
      {tab === "preachers"  && <PreachersTab preachers={preachers} setPreachers={setPreachers} visits={visits} newMuslims={newMuslims} showToast={showToast} />}
      {tab === "reports"    && <ReportsTab residences={residences} workers={workers} visits={visits} programs={programs} newMuslims={newMuslims} preachers={preachers} showToast={showToast} />}
      {tab === "alerts"     && <AlertsTab residences={residences} newMuslims={newMuslims} programs={programs} />}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Overview Tab
   ═════════════════════════════════════════════ */
function OverviewTab({ residences, workers, visits, programs, newMuslims, preachers }) {
  const totalResidences = residences.length;
  const totalWorkers = workers.length;
  const fullResidences = residences.filter(r => r.status === "ممتلئ" || (r.capacity > 0 && r.currentOccupancy >= r.capacity)).length;
  const visitsThisMonth = visits.filter(v => {
    const d = new Date(v.date);
    return d.getFullYear() === TODAY_REF.getFullYear() && d.getMonth() === TODAY_REF.getMonth();
  }).length;
  const executedPrograms = programs.filter(p => p.status === "منتهي" || p.status === "جاري").length;
  const newMuslimsCount = newMuslims.length;
  const activePreachers = preachers.filter(p => p.status === "نشطة").length;
  const followUpCount = newMuslims.filter(n => n.followUpStatus === "تحت المتابعة").length;

  const nationalityPalette = ["#0891b2", C.teal, "#7c3aed", C.accent, "#ea580c", C.success, C.danger, C.primary];
  const workersByNationality = useMemo(() => {
    const map = {};
    workers.forEach(w => { map[w.nationality] = (map[w.nationality] || 0) + 1; });
    return Object.keys(map).map((name, i) => ({
      name, value: map[name], color: nationalityPalette[i % nationalityPalette.length],
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workers]);

  const visitsByMonth = useMemo(() => {
    const year = TODAY_REF.getFullYear();
    return Array.from({ length: 12 }, (_, i) => ({
      name: monthLabelAR(i),
      value: visits.filter(v => {
        const d = new Date(v.date);
        return d.getFullYear() === year && d.getMonth() === i;
      }).length,
    }));
  }, [visits]);

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.kpiRow}>
        <KPICard title="عدد المساكن"            value={totalResidences}    icon={Building2}    color="#0891b2" />
        <KPICard title="إجمالي العاملات"        value={totalWorkers}       icon={Users}        color={C.teal} />
        <KPICard title="المساكن الممتلئة"       value={fullResidences}     icon={Home}         color={C.accent} />
        <KPICard title="الزيارات هذا الشهر"     value={visitsThisMonth}    icon={Calendar}     color="#7c3aed" />
        <KPICard title="البرامج المنفذة"        value={executedPrograms}   icon={BookOpen}     color={C.primary} />
        <KPICard title="المسلمات الجدد"         value={newMuslimsCount}    icon={Star}         color={C.success} />
        <KPICard title="الداعيات النشطات"       value={activePreachers}    icon={Mic}          color="#ea580c" />
        <KPICard title="المستفيدات تحت المتابعة" value={followUpCount}      icon={UserCheck}    color={C.warning} />
      </div>

      <div style={styles.chartsRow}>
        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PieChartIcon size={18} color={C.teal} />
              <span style={styles.chartTitle}>توزيع العاملات حسب الجنسية</span>
            </div>
          </div>
          {workersByNationality.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie data={workersByNationality} cx="50%" cy="50%" innerRadius={55} outerRadius={90} dataKey="value" paddingAngle={3}>
                    {workersByNationality.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                    formatter={(v) => [`${v} عاملة`]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div style={styles.legendGrid}>
                {workersByNationality.map((d, i) => (
                  <div key={i} style={styles.legendItem}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                    <span style={{ fontSize: 11, color: C.textLight }}>{d.name}</span>
                    <span style={{ fontSize: 11, fontWeight: 600, color: C.primary }}>{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div style={styles.emptyChart}>لا توجد بيانات عاملات بعد.</div>
          )}
        </Card>

        <Card style={styles.chartCard}>
          <div style={styles.chartHeader}>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <BarChart3 size={18} color={C.primary} />
              <span style={styles.chartTitle}>الزيارات شهرياً ({TODAY_REF.getFullYear()})</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={visitsByMonth} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="name" fontSize={10} tick={{ fill: C.textLight }} />
              <YAxis fontSize={11} tick={{ fill: C.textLight }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: 8, border: `1px solid ${C.border}`, fontFamily: "'Readex Pro'" }}
                formatter={(v) => [`${v} زيارة`]}
              />
              <Bar dataKey="value" name="الزيارات" fill={C.teal} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Residences Tab
   ═════════════════════════════════════════════ */
function ResidencesTab({ residences, setResidences, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_RESIDENCE);
  const [filterDistrict, setFilterDistrict] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = residences
    .filter(r => filterDistrict === "all" || r.district === filterDistrict)
    .filter(r => filterStatus === "all" || r.status === filterStatus)
    .filter(r => !search || r.name.includes(search) || r.code.includes(search) || r.supervisorName.includes(search));

  async function handleSave() {
    if (!form.name || !form.code || !form.district) {
      showToast("يرجى تعبئة الحقول المطلوبة", "error");
      return;
    }
    const payload = {
      code: form.code,
      name: form.name,
      district: form.district,
      address: form.address || "",
      capacity: Number(form.capacity) || 0,
      current_occupancy: Number(form.currentOccupancy) || 0,
      supervisor_name: form.supervisorName || "",
      supervisor_phone: form.supervisorPhone || "",
      type: form.type || "",
      status: form.status || "",
      notes: form.notes || "",
    };
    const { data, error } = await createHousingResidence(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) setResidences(prev => [mapResidenceRow(data), ...prev]);
    setForm(INITIAL_RESIDENCE);
    setShowAdd(false);
    showToast("تم إضافة السكن بنجاح");
  }

  const columns = [
    { key: "code",      label: "الكود",         nowrap: true },
    { key: "name",      label: "الاسم",         nowrap: true },
    { key: "district",  label: "الحي" },
    { key: "type",      label: "النوع",         nowrap: true },
    { key: "capacity",  label: "السعة",         nowrap: true },
    { key: "currentOccupancy", label: "الإشغال", nowrap: true, render: (v, row) => `${v}/${row.capacity}` },
    { key: "supervisorName", label: "المشرفة" },
    { key: "supervisorPhone", label: "الجوال",  nowrap: true },
    { key: "status",    label: "الحالة",        nowrap: true, render: v => <StatusBadge status={v} statuses={RESIDENCE_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الكود أو المشرفة..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterDistrict} onChange={e => setFilterDistrict(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الأحياء</option>
              {DISTRICTS.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">جميع الحالات</option>
              {RESIDENCE_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة سكن</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة سكن جديد" onClose={() => setShowAdd(false)} width={720}>
          <div style={styles.formGrid2}>
            <Field label="الكود" value={form.code} onChange={v => setForm({ ...form, code: v })} required />
            <Field label="اسم السكن" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
            <Field label="الحي" type="select" value={form.district} onChange={v => setForm({ ...form, district: v })} options={DISTRICTS} required />
            <Field label="العنوان" value={form.address} onChange={v => setForm({ ...form, address: v })} />
            <Field label="النوع" type="select" value={form.type} onChange={v => setForm({ ...form, type: v })} options={RESIDENCE_TYPES} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={RESIDENCE_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
            <Field label="السعة" type="number" min="0" value={form.capacity} onChange={v => setForm({ ...form, capacity: v })} />
            <Field label="الإشغال الحالي" type="number" min="0" value={form.currentOccupancy} onChange={v => setForm({ ...form, currentOccupancy: v })} />
            <Field label="اسم المشرفة" value={form.supervisorName} onChange={v => setForm({ ...form, supervisorName: v })} />
            <Field label="جوال المشرفة" value={form.supervisorPhone} onChange={v => setForm({ ...form, supervisorPhone: v })} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Workers Tab
   ═════════════════════════════════════════════ */
function WorkersTab({ residences, workers, setWorkers, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_WORKER);
  const [filterNationality, setFilterNationality] = useState("all");
  const [filterResidence, setFilterResidence] = useState("all");
  const [filterReligion, setFilterReligion] = useState("all");
  const [search, setSearch] = useState("");

  const filtered = workers
    .filter(w => filterNationality === "all" || w.nationality === filterNationality)
    .filter(w => filterResidence === "all" || w.residenceId === filterResidence)
    .filter(w => filterReligion === "all" || w.religion === filterReligion)
    .filter(w => !search || w.name.includes(search) || w.employer.includes(search) || w.phone.includes(search));

  async function handleSave() {
    if (!form.name || !form.residenceId) {
      showToast("الاسم والسكن مطلوبان", "error");
      return;
    }
    const res = residences.find(r => r.id === form.residenceId);
    const payload = {
      residence_id: form.residenceId,
      residence_name: res ? res.name : "",
      name: form.name,
      nationality: form.nationality || "",
      language: form.language || "",
      age: Number(form.age) || 0,
      job: form.job || "",
      employer: form.employer || "",
      phone: form.phone || "",
      religion: form.religion || "",
      entry_date: form.entryDate || null,
      status: form.status || "",
      notes: form.notes || "",
    };
    const { data, error } = await createHousingWorker(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) setWorkers(prev => [mapWorkerRow(data), ...prev]);
    setForm(INITIAL_WORKER);
    setShowAdd(false);
    showToast("تم إضافة العاملة بنجاح");
  }

  const columns = [
    { key: "name",          label: "الاسم", nowrap: true },
    { key: "nationality",   label: "الجنسية", nowrap: true },
    { key: "language",      label: "اللغة", nowrap: true },
    { key: "age",           label: "العمر", nowrap: true },
    { key: "job",           label: "العمل", nowrap: true },
    { key: "religion",      label: "الديانة", nowrap: true },
    { key: "residenceName", label: "السكن" },
    { key: "employer",      label: "صاحب العمل" },
    { key: "phone",         label: "الجوال", nowrap: true },
    { key: "entryDate",     label: "تاريخ الدخول", nowrap: true },
    { key: "status",        label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={WORKER_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو صاحب العمل..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterNationality} onChange={e => setFilterNationality(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل الجنسيات</option>
              {NATIONALITIES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
            <select value={filterResidence} onChange={e => setFilterResidence(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل المساكن</option>
              {residences.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select value={filterReligion} onChange={e => setFilterReligion(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل الديانات</option>
              {RELIGIONS.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة عاملة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة عاملة" onClose={() => setShowAdd(false)} width={720}>
          <div style={styles.formGrid2}>
            <Field label="السكن" type="select" value={form.residenceId} onChange={v => setForm({ ...form, residenceId: v })} options={residences.map(r => ({ value: r.id, label: r.name }))} required />
            <Field label="الاسم" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
            <Field label="الجنسية" type="select" value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} options={NATIONALITIES} />
            <Field label="اللغة" type="select" value={form.language} onChange={v => setForm({ ...form, language: v })} options={LANGUAGES} />
            <Field label="العمر" type="number" min="18" max="80" value={form.age} onChange={v => setForm({ ...form, age: v })} />
            <Field label="العمل" type="select" value={form.job} onChange={v => setForm({ ...form, job: v })} options={JOBS} />
            <Field label="صاحب العمل" value={form.employer} onChange={v => setForm({ ...form, employer: v })} />
            <Field label="الجوال" value={form.phone} onChange={v => setForm({ ...form, phone: v })} />
            <Field label="الديانة" type="select" value={form.religion} onChange={v => setForm({ ...form, religion: v })} options={RELIGIONS} />
            <Field label="تاريخ الدخول" type="date" value={form.entryDate} onChange={v => setForm({ ...form, entryDate: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={WORKER_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Visits Tab — scope=own للداعية
   ═════════════════════════════════════════════ */
function VisitsTab({ residences, preachers, visits, setVisits, user, perms, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_VISIT);
  const [filterResidence, setFilterResidence] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterPreacher, setFilterPreacher] = useState("all");

  // ✦ Scope: الداعية ترى زياراتها فقط
  const scoped = filterByOwner(visits, user, perms);

  const filtered = scoped
    .filter(v => filterResidence === "all" || v.residenceId === filterResidence)
    .filter(v => filterType === "all" || v.type === filterType)
    .filter(v => filterPreacher === "all" || v.preacherId === filterPreacher);

  async function handleSave() {
    if (!form.residenceId || !form.date || !form.preacherId) {
      showToast("الرجاء تعبئة السكن والتاريخ والداعية", "error");
      return;
    }
    const res = residences.find(r => r.id === form.residenceId);
    const pre = preachers.find(p => p.id === form.preacherId);
    const payload = {
      residence_id: form.residenceId,
      residence_name: res ? res.name : "",
      date: form.date || null,
      time: form.time || null,
      preacher_id: form.preacherId,
      preacher_name: pre ? pre.name : "",
      type: form.type || "",
      attendees_count: Number(form.attendeesCount) || 0,
      topics_covered: form.topicsCovered || "",
      outcome: form.outcome || "",
      notes: form.notes || "",
    };
    const { data, error } = await createHousingVisit(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) setVisits(prev => [mapVisitRow(data), ...prev]);
    setForm(INITIAL_VISIT);
    setShowAdd(false);
    showToast("تم تسجيل الزيارة بنجاح");
  }

  const columns = [
    { key: "date",           label: "التاريخ", nowrap: true },
    { key: "time",           label: "الوقت",  nowrap: true },
    { key: "residenceName",  label: "السكن" },
    { key: "preacherName",   label: "الداعية" },
    { key: "type",           label: "النوع",  nowrap: true },
    { key: "attendeesCount", label: "الحضور", nowrap: true },
    { key: "topicsCovered",  label: "الموضوعات" },
    { key: "outcome",        label: "النتيجة" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {perms?.scope === "own" && (
        <Card style={{ padding: 12, marginBottom: 16, background: `${C.teal}10`, borderRight: `3px solid ${C.teal}` }}>
          <div style={{ fontSize: 13, color: C.primary, display: "flex", alignItems: "center", gap: 8 }}>
            <UserCheck size={16} color={C.teal} />
            <span>أنتِ ترين زياراتك الخاصة فقط (نطاق: داعية).</span>
          </div>
        </Card>
      )}

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterResidence} onChange={e => setFilterResidence(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل المساكن</option>
              {residences.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل الأنواع</option>
              {VISIT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterPreacher} onChange={e => setFilterPreacher(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل الداعيات</option>
              {preachers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة زيارة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="تسجيل زيارة" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="السكن" type="select" value={form.residenceId} onChange={v => setForm({ ...form, residenceId: v })} options={residences.map(r => ({ value: r.id, label: r.name }))} required />
            <Field label="الداعية" type="select" value={form.preacherId} onChange={v => setForm({ ...form, preacherId: v })} options={preachers.map(p => ({ value: p.id, label: p.name }))} required />
            <Field label="التاريخ" type="date" value={form.date} onChange={v => setForm({ ...form, date: v })} required />
            <Field label="الوقت" type="time" value={form.time} onChange={v => setForm({ ...form, time: v })} />
            <Field label="النوع" type="select" value={form.type} onChange={v => setForm({ ...form, type: v })} options={VISIT_TYPES} />
            <Field label="عدد الحضور" type="number" min="0" value={form.attendeesCount} onChange={v => setForm({ ...form, attendeesCount: v })} />
          </div>
          <Field label="الموضوعات المطروحة" type="textarea" value={form.topicsCovered} onChange={v => setForm({ ...form, topicsCovered: v })} />
          <Field label="النتيجة" type="textarea" value={form.outcome} onChange={v => setForm({ ...form, outcome: v })} />
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Programs Tab
   ═════════════════════════════════════════════ */
function ProgramsTab({ residences, preachers, programs, setPrograms, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PROGRAM);
  const [filterResidence, setFilterResidence] = useState("all");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");

  const filtered = programs
    .filter(p => filterResidence === "all" || p.residenceId === filterResidence)
    .filter(p => filterType === "all" || p.type === filterType)
    .filter(p => filterStatus === "all" || p.status === filterStatus);

  async function handleSave() {
    if (!form.name || !form.residenceId) {
      showToast("الاسم والسكن مطلوبان", "error");
      return;
    }
    const res = residences.find(r => r.id === form.residenceId);
    const payload = {
      residence_id: form.residenceId,
      residence_name: res ? res.name : "",
      name: form.name,
      type: form.type || "",
      preacher_name: form.preacherName || "",
      start_date: form.startDate || null,
      sessions_count: Number(form.sessionsCount) || 0,
      beneficiaries_count: Number(form.beneficiariesCount) || 0,
      status: form.status || "",
      notes: form.notes || "",
    };
    const { data, error } = await createHousingProgram(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) setPrograms(prev => [mapProgramRow(data), ...prev]);
    setForm(INITIAL_PROGRAM);
    setShowAdd(false);
    showToast("تم إضافة البرنامج بنجاح");
  }

  const columns = [
    { key: "name",               label: "اسم البرنامج" },
    { key: "type",               label: "النوع",   nowrap: true },
    { key: "residenceName",      label: "السكن" },
    { key: "preacherName",       label: "الداعية" },
    { key: "startDate",          label: "تاريخ البدء", nowrap: true },
    { key: "sessionsCount",      label: "الجلسات", nowrap: true },
    { key: "beneficiariesCount", label: "المستفيدات", nowrap: true },
    { key: "status",             label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={PROGRAM_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterResidence} onChange={e => setFilterResidence(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل المساكن</option>
              {residences.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل الأنواع</option>
              {PROGRAM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل الحالات</option>
              {PROGRAM_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة برنامج</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة برنامج" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="اسم البرنامج" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
            <Field label="النوع" type="select" value={form.type} onChange={v => setForm({ ...form, type: v })} options={PROGRAM_TYPES} />
            <Field label="السكن" type="select" value={form.residenceId} onChange={v => setForm({ ...form, residenceId: v })} options={residences.map(r => ({ value: r.id, label: r.name }))} required />
            <Field label="الداعية" type="select" value={form.preacherName} onChange={v => setForm({ ...form, preacherName: v })} options={preachers.map(p => p.name)} />
            <Field label="تاريخ البدء" type="date" value={form.startDate} onChange={v => setForm({ ...form, startDate: v })} />
            <Field label="عدد الجلسات" type="number" min="0" value={form.sessionsCount} onChange={v => setForm({ ...form, sessionsCount: v })} />
            <Field label="عدد المستفيدات" type="number" min="0" value={form.beneficiariesCount} onChange={v => setForm({ ...form, beneficiariesCount: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={PROGRAM_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ New Muslims Tab — scope=own للداعية
   ═════════════════════════════════════════════ */
function NewMuslimsTab({ residences, preachers, newMuslims, setNewMuslims, user, perms, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_NEW_MUSLIM);
  const [filterResidence, setFilterResidence] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPreacher, setFilterPreacher] = useState("all");

  // ✦ Scope: الداعية ترى مستفيداتها فقط
  const scoped = filterByOwner(newMuslims, user, perms);

  const filtered = scoped
    .filter(n => filterResidence === "all" || n.residenceId === filterResidence)
    .filter(n => filterStatus === "all" || n.followUpStatus === filterStatus)
    .filter(n => filterPreacher === "all" || n.preacherId === filterPreacher);

  async function handleSave() {
    if (!form.nameAfter || !form.residenceId || !form.preacherId) {
      showToast("الرجاء تعبئة الاسم الجديد والسكن والداعية", "error");
      return;
    }
    const res = residences.find(r => r.id === form.residenceId);
    const pre = preachers.find(p => p.id === form.preacherId);
    const payload = {
      residence_id: form.residenceId,
      residence_name: res ? res.name : "",
      name_before: form.nameBefore || "",
      name_after: form.nameAfter,
      nationality: form.nationality || "",
      language: form.language || "",
      conversion_date: form.conversionDate || null,
      preacher_id: form.preacherId,
      preacher_name: pre ? pre.name : "",
      education_program: form.educationProgram || "",
      follow_up_status: form.followUpStatus || "",
      certificate: {
        issued: !!form.certIssued,
        number: form.certNumber || "",
        issueDate: form.certIssueDate || "",
        delivered: !!form.certDelivered,
      },
      notes: form.notes || "",
    };
    const { data, error } = await createHousingNewMuslim(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) setNewMuslims(prev => [mapNewMuslimRow(data), ...prev]);
    setForm(INITIAL_NEW_MUSLIM);
    setShowAdd(false);
    showToast("تم تسجيل المسلمة الجديدة بنجاح");
  }

  const columns = [
    { key: "nameAfter",       label: "الاسم الجديد", nowrap: true },
    { key: "nameBefore",      label: "الاسم السابق", nowrap: true },
    { key: "nationality",     label: "الجنسية",       nowrap: true },
    { key: "language",        label: "اللغة",         nowrap: true },
    { key: "residenceName",   label: "السكن" },
    { key: "preacherName",    label: "الداعية" },
    { key: "conversionDate",  label: "تاريخ الإسلام", nowrap: true },
    { key: "educationProgram", label: "البرنامج التعليمي" },
    { key: "followUpStatus",  label: "حالة المتابعة", nowrap: true, render: v => <StatusBadge status={v} statuses={FOLLOWUP_STATUSES} /> },
    { key: "certificate",     label: "الشهادة",       nowrap: true, render: c => c?.issued ? (c.delivered ? "مُسلَّمة" : `جاهزة (${c.number || "—"})`) : "—" },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      {perms?.scope === "own" && (
        <Card style={{ padding: 12, marginBottom: 16, background: `${C.success}10`, borderRight: `3px solid ${C.success}` }}>
          <div style={{ fontSize: 13, color: C.primary, display: "flex", alignItems: "center", gap: 8 }}>
            <UserCheck size={16} color={C.success} />
            <span>أنتِ ترين المستفيدات اللواتي تابعتِهن فقط (نطاق: داعية).</span>
          </div>
        </Card>
      )}

      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <select value={filterResidence} onChange={e => setFilterResidence(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل المساكن</option>
              {residences.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
            </select>
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل الحالات</option>
              {FOLLOWUP_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <select value={filterPreacher} onChange={e => setFilterPreacher(e.target.value)} style={styles.filterSelect}>
              <option value="all">كل الداعيات</option>
              {preachers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>تسجيل مسلمة جديدة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="تسجيل مسلمة جديدة" onClose={() => setShowAdd(false)} width={760}>
          <div style={styles.formGrid2}>
            <Field label="الاسم السابق" value={form.nameBefore} onChange={v => setForm({ ...form, nameBefore: v })} />
            <Field label="الاسم الجديد" value={form.nameAfter} onChange={v => setForm({ ...form, nameAfter: v })} required />
            <Field label="الجنسية" type="select" value={form.nationality} onChange={v => setForm({ ...form, nationality: v })} options={NATIONALITIES} />
            <Field label="اللغة" type="select" value={form.language} onChange={v => setForm({ ...form, language: v })} options={LANGUAGES} />
            <Field label="السكن" type="select" value={form.residenceId} onChange={v => setForm({ ...form, residenceId: v })} options={residences.map(r => ({ value: r.id, label: r.name }))} required />
            <Field label="الداعية المتابعة" type="select" value={form.preacherId} onChange={v => setForm({ ...form, preacherId: v })} options={preachers.map(p => ({ value: p.id, label: p.name }))} required />
            <Field label="تاريخ الإسلام" type="date" value={form.conversionDate} onChange={v => setForm({ ...form, conversionDate: v })} />
            <Field label="البرنامج التعليمي" value={form.educationProgram} onChange={v => setForm({ ...form, educationProgram: v })} />
            <Field label="حالة المتابعة" type="select" value={form.followUpStatus} onChange={v => setForm({ ...form, followUpStatus: v })} options={FOLLOWUP_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>

          <div style={styles.sectionDivider}>شهادة إعلان الإسلام</div>
          <div style={styles.formGrid2}>
            <Field label="رقم الشهادة" value={form.certNumber} onChange={v => setForm({ ...form, certNumber: v })} placeholder="مثال: C-2026-100" />
            <Field label="تاريخ الإصدار" type="date" value={form.certIssueDate} onChange={v => setForm({ ...form, certIssueDate: v })} />
            <Field label="صدرت؟" type="select" value={form.certIssued ? "نعم" : "لا"} onChange={v => setForm({ ...form, certIssued: v === "نعم" })} options={["نعم", "لا"]} />
            <Field label="تم التسليم؟" type="select" value={form.certDelivered ? "نعم" : "لا"} onChange={v => setForm({ ...form, certDelivered: v === "نعم" })} options={["نعم", "لا"]} />
          </div>

          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Education Tab
   ═════════════════════════════════════════════ */
function EducationTab({ education, setEducation, showToast }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ shahada: 0, taharah: 0, salah: 0, iman: 0, recitation: 0 });

  function openEdit(row) {
    setEditing(row);
    setForm({
      shahada: row.shahada, taharah: row.taharah, salah: row.salah, iman: row.iman, recitation: row.recitation,
    });
  }

  async function handleSave() {
    const patch = {
      shahada: Number(form.shahada) || 0,
      taharah: Number(form.taharah) || 0,
      salah: Number(form.salah) || 0,
      iman: Number(form.iman) || 0,
      recitation: Number(form.recitation) || 0,
      last_update: TODAY_REF.toISOString().slice(0, 10),
    };
    const { data, error } = await updateHousingEducation(editing.id, patch);
    if (error) { showToast(error, "error"); return; }
    if (data) setEducation(prev => prev.map(r => r.id === editing.id ? mapEducationRow(data) : r));
    setEditing(null);
    showToast("تم تحديث التقدم بنجاح");
  }

  function bar(value, color) {
    const v = Math.max(0, Math.min(100, Number(value) || 0));
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 110 }}>
        <div style={{ flex: 1, height: 8, borderRadius: 4, background: `${color}20`, overflow: "hidden" }}>
          <div style={{ width: `${v}%`, height: "100%", background: color, borderRadius: 4 }} />
        </div>
        <span style={{ fontSize: 11, fontWeight: 600, color: C.primary, minWidth: 32 }}>{v}%</span>
      </div>
    );
  }

  const columns = [
    { key: "beneficiaryName", label: "المستفيدة", nowrap: true },
    { key: "residenceName",   label: "السكن" },
    { key: "shahada",         label: "الشهادتان",  render: v => bar(v, C.success) },
    { key: "taharah",         label: "الطهارة",     render: v => bar(v, "#0891b2") },
    { key: "salah",           label: "الصلاة",      render: v => bar(v, C.teal) },
    { key: "iman",            label: "أركان الإيمان", render: v => bar(v, C.primary) },
    { key: "recitation",      label: "التلاوة",     render: v => bar(v, C.accent) },
    { key: "lastUpdate",      label: "آخر تحديث",   nowrap: true },
    { key: "actions",         label: "إجراء", nowrap: true, render: (_, row) => (
      <Btn size="sm" variant="ghost" onClick={() => openEdit(row)}>تحديث</Btn>
    )},
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: C.textLight }}>
          متابعة تقدم المسلمات الجدد عبر خمسة مستويات أساسية. اضغطي "تحديث" لتعديل النِسَب.
        </div>
      </Card>

      <DataTable columns={columns} data={education} />

      {editing && (
        <Modal title={`تحديث تقدم: ${editing.beneficiaryName}`} onClose={() => setEditing(null)} width={620}>
          <div style={styles.formGrid2}>
            <Field label="النطق بالشهادتين %" type="number" min="0" max="100" value={form.shahada} onChange={v => setForm({ ...form, shahada: v })} />
            <Field label="الطهارة والوضوء %" type="number" min="0" max="100" value={form.taharah} onChange={v => setForm({ ...form, taharah: v })} />
            <Field label="الصلاة %" type="number" min="0" max="100" value={form.salah} onChange={v => setForm({ ...form, salah: v })} />
            <Field label="أركان الإيمان %" type="number" min="0" max="100" value={form.iman} onChange={v => setForm({ ...form, iman: v })} />
            <Field label="التلاوة %" type="number" min="0" max="100" value={form.recitation} onChange={v => setForm({ ...form, recitation: v })} />
          </div>
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setEditing(null)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Materials Tab
   ═════════════════════════════════════════════ */
function MaterialsTab({ residences, materials, setMaterials, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_MATERIAL);
  const [filterCategory, setFilterCategory] = useState("all");

  const filtered = materials.filter(m => filterCategory === "all" || m.category === filterCategory);

  async function handleSave() {
    if (!form.item || !form.residenceId) {
      showToast("المادة والسكن مطلوبان", "error");
      return;
    }
    const res = residences.find(r => r.id === form.residenceId);
    const payload = {
      residence_id: form.residenceId,
      residence_name: res ? res.name : "",
      item: form.item,
      category: form.category || "",
      quantity: Number(form.quantity) || 0,
      distributed_date: form.distributedDate || null,
      distributed_by: form.distributedBy || "",
      recipients_count: Number(form.recipientsCount) || 0,
      notes: form.notes || "",
    };
    const { data, error } = await createHousingMaterial(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) setMaterials(prev => [mapMaterialRow(data), ...prev]);
    setForm(INITIAL_MATERIAL);
    setShowAdd(false);
    showToast("تم تسجيل المادة/الهدية بنجاح");
  }

  const columns = [
    { key: "item",             label: "المادة" },
    { key: "category",         label: "الفئة",   nowrap: true },
    { key: "residenceName",    label: "السكن" },
    { key: "quantity",         label: "الكمية", nowrap: true, render: v => (v || 0).toLocaleString("ar-SA") },
    { key: "distributedDate",  label: "تاريخ التوزيع", nowrap: true },
    { key: "distributedBy",    label: "بواسطة" },
    { key: "recipientsCount",  label: "المستفيدات", nowrap: true },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} style={styles.filterSelect}>
            <option value="all">كل الفئات</option>
            {MATERIAL_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة مادة</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة مادة/هدية" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="السكن" type="select" value={form.residenceId} onChange={v => setForm({ ...form, residenceId: v })} options={residences.map(r => ({ value: r.id, label: r.name }))} required />
            <Field label="المادة" value={form.item} onChange={v => setForm({ ...form, item: v })} required />
            <Field label="الفئة" type="select" value={form.category} onChange={v => setForm({ ...form, category: v })} options={MATERIAL_CATEGORIES} />
            <Field label="الكمية" type="number" min="0" value={form.quantity} onChange={v => setForm({ ...form, quantity: v })} />
            <Field label="تاريخ التوزيع" type="date" value={form.distributedDate} onChange={v => setForm({ ...form, distributedDate: v })} />
            <Field label="بواسطة" value={form.distributedBy} onChange={v => setForm({ ...form, distributedBy: v })} />
            <Field label="عدد المستفيدات" type="number" min="0" value={form.recipientsCount} onChange={v => setForm({ ...form, recipientsCount: v })} />
          </div>
          <Field label="ملاحظات" type="textarea" value={form.notes} onChange={v => setForm({ ...form, notes: v })} />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Preachers Tab
   ═════════════════════════════════════════════ */
function PreachersTab({ preachers, setPreachers, visits, newMuslims, showToast }) {
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(INITIAL_PREACHER);
  const [search, setSearch] = useState("");

  const enriched = preachers.map(p => ({
    ...p,
    languagesText: (p.languages || []).join("، "),
    assignedText: (p.assignedResidences || []).join("، "),
    visitsCount: visits.filter(v => v.preacherId === p.id).length,
    newMuslimsCount: newMuslims.filter(n => n.preacherId === p.id).length,
  }));

  const filtered = enriched.filter(p => !search || p.name.includes(search) || p.phone.includes(search) || (p.email || "").includes(search));

  async function handleSave() {
    if (!form.name || !form.phone) {
      showToast("الاسم والجوال مطلوبان", "error");
      return;
    }
    const langs = (form.languages || "").split(/[،,]+/).map(s => s.trim()).filter(Boolean);
    const resns = (form.assignedResidences || "").split(/[،,]+/).map(s => s.trim()).filter(Boolean);
    const payload = {
      name: form.name,
      phone: form.phone,
      email: form.email || "",
      languages: [...langs],
      assigned_residences: [...resns],
      status: form.status || "",
    };
    const { data, error } = await createHousingPreacher(payload);
    if (error) { showToast(error, "error"); return; }
    if (data) setPreachers(prev => [mapPreacherRow(data), ...prev]);
    setForm(INITIAL_PREACHER);
    setShowAdd(false);
    showToast("تم إضافة الداعية بنجاح");
  }

  const columns = [
    { key: "name",            label: "الاسم", nowrap: true },
    { key: "phone",           label: "الجوال", nowrap: true },
    { key: "email",           label: "البريد" },
    { key: "languagesText",   label: "اللغات" },
    { key: "assignedText",    label: "المساكن المعيّنة" },
    { key: "visitsCount",     label: "الزيارات", nowrap: true },
    { key: "newMuslimsCount", label: "المسلمات الجدد", nowrap: true },
    { key: "status",          label: "الحالة", nowrap: true, render: v => <StatusBadge status={v} statuses={PREACHER_STATUSES} /> },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <Card style={{ padding: 16, marginBottom: 16 }}>
        <div style={styles.toolbar}>
          <div style={styles.searchBox}>
            <Search size={16} color={C.textLight} />
            <input type="text" placeholder="بحث بالاسم أو الجوال..." value={search} onChange={e => setSearch(e.target.value)} style={styles.searchInput} />
          </div>
          <Btn icon={Plus} onClick={() => setShowAdd(true)}>إضافة داعية</Btn>
        </div>
      </Card>

      <DataTable columns={columns} data={filtered} />

      {showAdd && (
        <Modal title="إضافة داعية" onClose={() => setShowAdd(false)} width={680}>
          <div style={styles.formGrid2}>
            <Field label="الاسم" value={form.name} onChange={v => setForm({ ...form, name: v })} required />
            <Field label="الجوال" value={form.phone} onChange={v => setForm({ ...form, phone: v })} required />
            <Field label="البريد" type="email" value={form.email} onChange={v => setForm({ ...form, email: v })} />
            <Field label="الحالة" type="select" value={form.status} onChange={v => setForm({ ...form, status: v })} options={PREACHER_STATUSES.map(s => ({ value: s.key, label: s.label }))} />
          </div>
          <Field label="اللغات (مفصولة بفواصل)" value={form.languages} onChange={v => setForm({ ...form, languages: v })} placeholder="مثال: إندونيسية، تاغالوغ" />
          <Field label="المساكن المعيّنة (مفصولة بفواصل)" value={form.assignedResidences} onChange={v => setForm({ ...form, assignedResidences: v })} placeholder="مثال: سكن النور، سكن الإيمان" />
          <div style={styles.modalActions}>
            <Btn variant="ghost" onClick={() => setShowAdd(false)}>إلغاء</Btn>
            <Btn onClick={handleSave} icon={Plus}>حفظ</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Reports Tab
   ═════════════════════════════════════════════ */
function ReportsTab({ residences, workers, visits, programs, newMuslims, preachers, showToast }) {
  const fullCount = residences.filter(r => r.capacity > 0 && r.currentOccupancy >= r.capacity).length;
  const completedFollowUp = newMuslims.filter(n => n.followUpStatus === "مكتمل").length;
  const totalAttendees = visits.reduce((s, v) => s + (Number(v.attendeesCount) || 0), 0);
  const activePreachers = preachers.filter(p => p.status === "نشطة").length;

  const cards = [
    {
      title: "تقرير شامل",
      summary: `${residences.length} مسكن · ${workers.length} عاملة · ${visits.length} زيارة · ${programs.length} برنامج · ${newMuslims.length} مسلمة جديدة`,
      color: C.primary, icon: BarChart3,
    },
    {
      title: "تقرير سكن محدد",
      summary: `إجمالي ${residences.length} مسكن — منها ${fullCount} ممتلئ، ${residences.filter(r => r.status === "تحت الصيانة").length} تحت الصيانة.`,
      color: "#0891b2", icon: Building2,
    },
    {
      title: "تقرير المسلمات الجدد",
      summary: `${newMuslims.length} مسلمة جديدة — ${completedFollowUp} مكتملة المتابعة و ${newMuslims.filter(n => n.followUpStatus === "تحت المتابعة").length} تحت المتابعة.`,
      color: C.success, icon: Star,
    },
    {
      title: "تقرير الزيارات",
      summary: `${visits.length} زيارة بإجمالي ${totalAttendees} حضور.`,
      color: C.teal, icon: Calendar,
    },
    {
      title: "تقرير الداعيات",
      summary: `${preachers.length} داعية — ${activePreachers} نشطة و ${preachers.length - activePreachers} متوقفة.`,
      color: C.accent, icon: Mic,
    },
  ];

  return (
    <div style={{ marginTop: 16 }}>
      <div style={styles.reportsGrid}>
        {cards.map((c, i) => {
          const IconCmp = c.icon;
          return (
            <Card key={i} style={styles.reportCard}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 8 }}>
                <div style={{ ...styles.reportIcon, background: `${c.color}20`, color: c.color }}>
                  <IconCmp size={22} color={c.color} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.primary }}>{c.title}</div>
              </div>
              <p style={{ fontSize: 12, color: C.textLight, margin: "4px 0 14px", lineHeight: 1.7 }}>
                {c.summary}
              </p>
              <Btn icon={Download} onClick={() => showToast(`تم تجهيز: ${c.title}`)}>توليد</Btn>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Alerts Tab
   ═════════════════════════════════════════════ */
function AlertsTab({ residences, newMuslims, programs }) {
  const nearFull = residences.filter(r => r.capacity > 0 && (r.currentOccupancy / r.capacity) >= 0.9 && r.status !== "تحت الصيانة" && r.status !== "مغلق");
  const followUpDropped = newMuslims.filter(n => {
    if (n.followUpStatus !== "منقطع") return false;
    return true;
  });
  // ولاحقاً: المنقطعات أكثر من 30 يوم — نعتمد على تاريخ آخر برنامج/زيارة. هنا نستخدم conversionDate كمرجع تقريبي.
  const longFollowUp = newMuslims.filter(n => n.followUpStatus === "تحت المتابعة" && daysSince(n.conversionDate) > 30);
  const lateProgram = programs.filter(p => {
    if (p.status !== "مخطط") return false;
    if (!p.startDate) return false;
    return daysSince(p.startDate) > 0;
  });
  const underMaintenance = residences.filter(r => r.status === "تحت الصيانة");

  const sections = [
    {
      key: "nearFull",
      title: "مساكن قاربت الامتلاء (≥90% إشغال)",
      color: C.warning,
      icon: Home,
      rows: nearFull,
      columns: [
        { key: "name",      label: "السكن", nowrap: true },
        { key: "district",  label: "الحي" },
        { key: "currentOccupancy", label: "الإشغال", nowrap: true, render: (v, row) => `${v}/${row.capacity} (${Math.round((v/row.capacity)*100)}%)` },
        { key: "supervisorName", label: "المشرفة" },
      ],
    },
    {
      key: "longFollowUp",
      title: "مستفيدات تحت المتابعة منذ أكثر من 30 يوماً",
      color: C.teal,
      icon: UserCheck,
      rows: longFollowUp.map(n => ({ ...n, days: daysSince(n.conversionDate) })),
      columns: [
        { key: "nameAfter",    label: "الاسم الجديد", nowrap: true },
        { key: "residenceName", label: "السكن" },
        { key: "preacherName",  label: "الداعية" },
        { key: "conversionDate", label: "تاريخ الإسلام", nowrap: true },
        { key: "days",         label: "عدد الأيام", nowrap: true, render: v => `${v} يوم` },
      ],
    },
    {
      key: "droppedFollowUp",
      title: "مستفيدات منقطعات عن المتابعة",
      color: C.danger,
      icon: AlertTriangle,
      rows: followUpDropped,
      columns: [
        { key: "nameAfter",    label: "الاسم الجديد", nowrap: true },
        { key: "residenceName", label: "السكن" },
        { key: "preacherName",  label: "الداعية" },
        { key: "notes",         label: "ملاحظة" },
      ],
    },
    {
      key: "lateProgram",
      title: "برامج متأخرة عن جدولها (مخططة وتاريخ البدء مرّ)",
      color: C.accent,
      icon: BookOpen,
      rows: lateProgram.map(p => ({ ...p, daysLate: daysSince(p.startDate) })),
      columns: [
        { key: "name",          label: "البرنامج" },
        { key: "residenceName", label: "السكن" },
        { key: "preacherName",  label: "الداعية" },
        { key: "startDate",     label: "تاريخ البدء", nowrap: true },
        { key: "daysLate",      label: "التأخير", nowrap: true, render: v => `${v} يوم` },
      ],
    },
    {
      key: "underMaintenance",
      title: "مساكن تحت الصيانة",
      color: C.warning,
      icon: Briefcase,
      rows: underMaintenance,
      columns: [
        { key: "name",      label: "السكن", nowrap: true },
        { key: "district",  label: "الحي" },
        { key: "capacity",  label: "السعة", nowrap: true },
        { key: "supervisorName", label: "المشرفة" },
        { key: "notes",     label: "ملاحظة" },
      ],
    },
  ];

  return (
    <div style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 18 }}>
      {sections.map(sec => {
        const IconCmp = sec.icon;
        return (
          <Card key={sec.key} style={{ padding: 16, borderRight: `4px solid ${sec.color}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: 10, background: `${sec.color}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <IconCmp size={18} color={sec.color} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: C.primary }}>{sec.title}</div>
                <div style={{ fontSize: 12, color: C.textLight }}>{sec.rows.length} عنصر</div>
              </div>
            </div>
            {sec.rows.length > 0
              ? <DataTable columns={sec.columns} data={sec.rows} />
              : <div style={{ fontSize: 13, color: C.textLight, padding: 12, textAlign: "center" }}>لا توجد تنبيهات في هذا القسم.</div>}
          </Card>
        );
      })}

      <Card style={{ padding: 12, background: `${C.primary}06`, display: "flex", alignItems: "center", gap: 10 }}>
        <ClipboardList size={16} color={C.primary} />
        <div style={{ fontSize: 12, color: C.textLight }}>
          تاريخ المرجع: <span style={{ color: C.primary, fontWeight: 600 }}>{TODAY_REF.toISOString().slice(0, 10)}</span>
          {" "}— التنبيهات محسوبة منطقياً من الحالة والتواريخ.
        </div>
      </Card>
    </div>
  );
}

/* ═════════════════════════════════════════════
   ✦ Styles
   ═════════════════════════════════════════════ */
const styles = {
  kpiRow: {
    display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
    gap: 20, marginBottom: 16,
  },
  chartsRow: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 340px), 1fr))",
    gap: 20, marginBottom: 20,
  },
  chartCard: { padding: 20 },
  chartHeader: {
    display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16,
  },
  chartTitle: { fontSize: 15, fontWeight: 700, color: C.primary },
  legendGrid: {
    display: "flex", flexWrap: "wrap", gap: 12,
    justifyContent: "center", marginTop: 8,
  },
  legendItem: {
    display: "flex", alignItems: "center", gap: 6,
    padding: "4px 10px", background: C.bg, borderRadius: 8,
  },
  emptyChart: {
    height: 240, display: "flex", alignItems: "center", justifyContent: "center",
    color: C.textLight, fontSize: 13,
  },

  toolbar: {
    display: "flex", justifyContent: "space-between",
    alignItems: "center", gap: 12, flexWrap: "wrap",
  },
  searchBox: {
    display: "flex", alignItems: "center", gap: 8, background: C.bg,
    borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}`,
    flex: 1, maxWidth: 320,
  },
  searchInput: {
    border: "none", background: "transparent", outline: "none",
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif", color: C.primary, width: "100%",
  },
  filterSelect: {
    padding: "7px 12px", borderRadius: 8, border: `1px solid ${C.border}`,
    fontSize: 13, fontFamily: "'Readex Pro', sans-serif",
    color: C.primary, background: C.white, cursor: "pointer",
  },

  formGrid2: {
    display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: 12, marginBottom: 8,
  },
  modalActions: {
    display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 18,
  },
  sectionDivider: {
    marginTop: 16, paddingBottom: 6, marginBottom: 8,
    borderBottom: `2px solid ${C.teal}30`,
    fontSize: 13, fontWeight: 700, color: C.teal,
  },

  reportsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))",
    gap: 16,
  },
  reportCard: { padding: 18, display: "flex", flexDirection: "column" },
  reportIcon: {
    width: 44, height: 44, borderRadius: 12,
    display: "flex", alignItems: "center", justifyContent: "center",
  },
};

